import { Button, SxProps, Theme } from "@mui/material";
import { ChangeEvent, MutableRefObject } from "react";
import SignaturePad from "signature_pad";
import { VisuallyHiddenInput } from "@/components/VisuallyHiddenInput";
import { useSnackbar } from "@/hooks/useSnackbar";
import { convertToBase64, getErrorSnackbar } from "@/utils/utils";
import {
  SIGNATURE_UPLOAD_ACCEPT,
  SIGNATURE_UPLOAD_ERROR,
  SIGNATURE_UPLOAD_LABEL,
  SIGNATURE_UPLOAD_MIME_TYPES,
} from "./signature-constant";

export interface UploadSignatureButtonProps {
  signPad?: SignaturePad;
  canvasRef: MutableRefObject<HTMLCanvasElement | null>;
  disabled?: boolean;
  onUploaded?: () => void;
  sx?: SxProps<Theme>;
}

const UploadSignatureButton = ({
  signPad,
  canvasRef,
  disabled,
  onUploaded,
  sx,
}: UploadSignatureButtonProps) => {
  const showSnackbar = useSnackbar();

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.item(0);
    event.target.value = "";
    if (!file || !signPad || !canvasRef.current) return;

    if (!SIGNATURE_UPLOAD_MIME_TYPES.includes(file.type)) {
      showSnackbar({ message: SIGNATURE_UPLOAD_ERROR, type: "error" });
      return;
    }

    try {
      const dataUrl = `data:${file.type};base64,${await convertToBase64(file)}`;
      const image = new Image();
      image.src = dataUrl;
      await image.decode();

      // Fit inside the pad keeping aspect ratio, so it is saved like a drawn signature.
      const { width, height } = canvasRef.current;
      const scale = Math.min(width / image.width, height / image.height);
      signPad.clear();
      await signPad.fromDataURL(dataUrl, {
        ratio: 1,
        width: image.width * scale,
        height: image.height * scale,
        xOffset: (width - image.width * scale) / 2,
        yOffset: (height - image.height * scale) / 2,
      });
      onUploaded?.();
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
    }
  };

  return (
    <Button
      component="label"
      variant="outlined"
      disabled={disabled}
      sx={[
        { height: "24px", bgcolor: "white" },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {SIGNATURE_UPLOAD_LABEL}
      <VisuallyHiddenInput
        type="file"
        accept={SIGNATURE_UPLOAD_ACCEPT}
        disabled={disabled}
        onChange={handleUpload}
      />
    </Button>
  );
};

export default UploadSignatureButton;
